import * as React from 'react';
import { connect } from 'react-redux';
import { isEqual, throttle, uniq } from 'lodash';

import Spinner from 'components/spinner/Spinner';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';

import BaseReportingPage from '../base/BaseReportingPage';
import ReportDownloadActions from '../../components/report-download-actions/ReportDownloadActions';
import ReportDownloadFilters from '../../components/report-download-filters/ReportDownloadFilters';
import DeliverablesRowsCustomReport from 'modules/subcampaigns/components/bulk/subcampaigns-deliverables-rows/SubcampaignsDeliverablesRowsCustom';

import ReportDownloadMetricsSummary from '../../components/report-download-metrics-summary/ReportDownloadMetricsSummary';
import CampaignReportDownloadButton from 'modules/campaigns/components/campaign-report-download-button/CampaignReportDownloadButton';

import {
  fetchCustomReportDeliverables,
  generateCustomReportAsync,
  resetCampaignCustomReport
} from 'modules/campaigns/reducers/custom-report/campaignCustomReport';
import { fetchSplits, resetSplits } from 'modules/reporting/reducers/campaign-cost-attribution';
import { fetchInfluncerMetrics } from 'modules/influencers/reducers/influencersMetrics';
import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';
import { filterCustomReportDate, getSelectedOnly } from 'utils/custom-report';
import { DELIVERABLE_TYPE_INSTAGRAM_POST, DELIVERABLE_TYPE_INSTAGRAM_STORY, DELIVERABLE_TYPE_TIKTOK_VIDEO } from 'utils/deliverables';

import type {
  CustomReportDeliverable,
  CustomReportDownloadType,
  CustomReportOffer,
  LocationWindow,
  Params
} from 'types';
import type { BudgetSplitResourceV2 } from 'modules/reporting/types/BudgetSplitResource.type';

import './report-download-page.scss';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./report-download-page.i18n.json'));

type OwnProps = {
  params: Params
};

type StateProps = {
  splits: Array<BudgetSplitResourceV2>,
  isSplitsLoading: boolean,
  isReportLoading: boolean,
  isDownloading: boolean,
  location: LocationWindow,
  groupedDeliverables: Array<CustomReportOffer>,
  campaignStartDate: string | undefined | null,
  campaignEndDate: string | undefined | null
};

type DispatchProps = {
  dispatchFetchSplits: (campaignId: number) => void,
  dispatchResetSplits: () => void,
  dispatchFetchCustomReportDeliverables: (campaignId: number) => Promise<void>,
  dispatchReset: () => Promise<void>,
  dispatchDownloadCustomReport: (
    campaignId: number,
    checkedIds: Array<number>,
    type: CustomReportDownloadType
  ) => Promise<void>,
  dispatchFetchInfluncerMetrics: (campaignId: number) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  startDate: string | undefined | null,
  endDate: string | undefined | null,
  searchQuery: string,
  isDisplaySelectedOnly: boolean,
  expandedIds: Array<number>,
  checkedIds: Array<number>,
  filteredData: Array<CustomReportOffer>,
  wasDataChecked: boolean // used to select all on load
};

class ReportDownloadPage extends React.PureComponent<Props, State> {
  throttledFilter: () => void;

  state: State = {
    startDate: null,
    endDate: null,
    searchQuery: '',
    expandedIds: [],
    checkedIds: [],
    filteredData: [],
    isDisplaySelectedOnly: false,
    wasDataChecked: false
  };

  constructor(props: Props) {
    super(props);

    this.throttledFilter = throttle(this.triggerFiltering, 1000);
  }

  componentDidMount(): void {
    const { params, dispatchFetchCustomReportDeliverables, dispatchFetchSplits, dispatchFetchInfluncerMetrics } = this.props;
    const campaignId = Number(params.id);

    dispatchFetchCustomReportDeliverables(campaignId);
    dispatchFetchSplits(campaignId);
    dispatchFetchInfluncerMetrics(campaignId);
  }

  componentDidUpdate(): void {
    const { groupedDeliverables } = this.props;
    const { wasDataChecked } = this.state;

    if (groupedDeliverables.length > 0 && !wasDataChecked) {
      this.triggerFiltering(); // trigger filter when deliverables are finally loaded
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        wasDataChecked: true
      }, this.handleSelectAll);
    }
  }

  componentWillUnmount(): void {
    const { dispatchReset, dispatchResetSplits } = this.props;

    dispatchReset();
    dispatchResetSplits();
  }

  getIdsToInclude = (): Array<number> => {
    const { checkedIds } = this.state;
    const dataToShow = this.getReportFilteredData();

    const deliverableIds = (dataToShow || []).reduce((acc, item) => {
      const ids = (item.deliverables || []).map(deliverable => deliverable.id);
      return acc.concat(ids);
    }, [] as Array<number>);

    return deliverableIds.filter(id => checkedIds.includes(id));
  };

  handleFiltersChange = (searchQuery: string, startDate?: string | null, endDate?: string | null) => {
    this.setState({
      searchQuery,
      startDate,
      endDate
    }, this.triggerFiltering);
  };

  triggerFiltering() {
    const { groupedDeliverables } = this.props;

    this.setState((state) => {
      const { startDate, endDate, searchQuery } = state;

      return {
        filteredData: filterCustomReportDate(groupedDeliverables, {
          startDate,
          endDate,
          searchQuery
        })
      };
    });
  }

  handleItemExpandToggle = (event: SyntheticEvent<HTMLElement>): void => {
    const target = event.currentTarget;
    const creatorId = Number(target.dataset.key);

    this.setState(state => {
      const shouldRemove = state.expandedIds.includes(creatorId);

      return {
        expandedIds: (shouldRemove)
          ? state.expandedIds.filter(id => id !== creatorId)
          : [...state.expandedIds, creatorId]
      };
    });
  };

  handleItemExpandToggleAll = (isExpand: boolean): void => {
    if (isExpand) {
      const ids = this.getReportFilteredData().map((entity) => entity.id);

      this.setState((state) => ({
        expandedIds: uniq(state.expandedIds.concat(ids))
      }));
    } else {
      this.setState({
        expandedIds: []
      });
    }
  };

  handleToggleSelectedOnly = (isChecked: boolean): void => {
    this.setState({
      isDisplaySelectedOnly: isChecked
    });
  };

  collectFilteredDeliverableIds(): Array<number> {
    return this.getReportFilteredData().reduce<Array<number>>((result, group: CustomReportOffer) => [
      ...result,
      ...group.deliverables
        .filter((deliverable) => deliverable.selectable)
        .map((deliverable) => deliverable.id)
    ], []);
  }

  handleSelectAll = () => {
    // select all regardless of current filters
    const deliverableIds = this.getAllDeviverableIds();

    this.setState((state) => ({
      checkedIds: uniq([...state.checkedIds, ...deliverableIds])
    }));
  };

  handleSelectAllMatched = () => {
    const deliverableIds = this.collectFilteredDeliverableIds();

    this.setState((state) => ({
      checkedIds: uniq([...state.checkedIds, ...deliverableIds])
    }));
  };

  handleDeSelectAllMatched = () => {
    // on select all (visible) we have to remove currently items applicable for current filters from uncheckedItems
    const deliverableIds = new Set(this.collectFilteredDeliverableIds());

    this.setState((state) => ({
      checkedIds: state.checkedIds.filter(id => !deliverableIds.has(id))
    }));
  };

  handleToggleAll = (selectAll: boolean): void => {
    try {
      if (selectAll) {
        this.handleSelectAllMatched();
      } else {
        this.handleDeSelectAllMatched();
      }
    } catch (e) {
      logError(e);
    }
  };

  handleCreatorToggle = (creatorId: number, isChecked: boolean): void => {
    const creatorEntry = this.getReportFilteredData()
      .find(entry => entry.id === creatorId);

    // need to keep only deliverables with supported type (IG post and story currently)
    const deliverableIds: Array<number> = creatorEntry
      ?.deliverables
      ?.filter((deliverable: CustomReportDeliverable) => deliverable.selectable)
      .map((deliverable: CustomReportDeliverable) => deliverable.id) || [];

    this.setState((state) => ({
      checkedIds: (isChecked)
        ? state.checkedIds.concat(deliverableIds)
        : state.checkedIds.filter(id => !deliverableIds.includes(id))
    }));
  };

  handleDeliverableToggle = (creatorId: number, deliverableId: number | string): void => {
    this.setState((state) => {
      const shouldRemove = state.checkedIds.includes(Number(deliverableId));

      return {
        checkedIds: (shouldRemove)
          ? state.checkedIds.filter(id => id !== deliverableId)
          : [...state.checkedIds, Number(deliverableId)]
      };
    });
  };

  handleDownloadExcel = (): void => {
    const { params, dispatchDownloadCustomReport } = this.props;
    const campaignId = Number(params.id);
    const idsToInclude = this.getIdsToInclude();

    dispatchDownloadCustomReport(campaignId, idsToInclude, 'spreadsheet');
    this.handleDownloadExcelEventTracking(idsToInclude);
  };

  handleDownloadPPT = (): void => {
    const { params, dispatchDownloadCustomReport } = this.props;
    const campaignId = Number(params.id);
    const idsToInclude = this.getIdsToInclude();

    dispatchDownloadCustomReport(campaignId, idsToInclude, 'presentation');
  };

  handleDownloadExcelEventTracking(idsToInclude: Array<number>): void {
    const fullList = this.getAllDeviverableIds();
    const eventLabel = isEqual(fullList, idsToInclude) ? 'regular' : 'customised';

    addUserAction({
      eventLabel,
      eventCategory: 'v2 reporting',
      eventAction: 'download excel'
    });
  }

  getAllDeviverableIds(): Array<number> {
    const { groupedDeliverables } = this.props;

    return groupedDeliverables.reduce<Array<number>>((result, group: CustomReportOffer) => [
      ...result,
      ...group.deliverables
        .filter((deliverable) => deliverable.selectable)
        .map((deliverable) => deliverable.id)
    ], []);
  }

  getReportFilteredData(): Array<CustomReportOffer> {
    const { groupedDeliverables } = this.props;
    const { filteredData, isDisplaySelectedOnly, checkedIds } = this.state;

    if (isDisplaySelectedOnly) {
      return getSelectedOnly(groupedDeliverables, checkedIds);
    }

    return filteredData;
  }

  renderCCARequiredState(): React.ReactNode {
    const title = __t('report_download_page.page_title');

    return (
      <BaseReportingPage
        headline={title}
        params={this.props.params}
      >
        <div className="report-download-page__message">
          {__t('report_download_page.cca_require_message')}
        </div>
      </BaseReportingPage>
    );
  }

  render(): React.ReactNode {
    const {
      params,
      location,
      splits,
      isSplitsLoading,
      isReportLoading,
      isDownloading,
      campaignStartDate,
      campaignEndDate,
      groupedDeliverables
    } = this.props;

    const {
      isDisplaySelectedOnly,
      expandedIds,
      checkedIds
    } = this.state;

    const campaignId = Number(params.id);
    const dataToShow = this.getReportFilteredData();

    const title = __t('report_download_page.page_title');

    if (isSplitsLoading || isReportLoading) {
      return (
        <BaseReportingPage
          headline={title}
          params={params}
        >
          <Spinner
            color="light"
            position="inline"
            size="small"
          />
        </BaseReportingPage>
      );
    }

    if (!isSplitsLoading && (splits.length === 0 || splits.some(split => split.budgetSplit.isOutdated))) {
      return this.renderCCARequiredState();
    }

    const isAlreadyExpanded = dataToShow.every((entity) => expandedIds.includes(entity.id));
    const isAlreadySelected = this.collectFilteredDeliverableIds().every((id) => checkedIds.includes(id));
    const isInstagramReport = dataToShow.some((offer) => {
      const selectedDeliverables = offer.deliverables
        .filter((deliverable) => deliverable.selectable && checkedIds.includes(deliverable.id));

      return selectedDeliverables.some((deliverable) => (
        deliverable.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_POST
        || deliverable.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_STORY
      ));
    });

    const isTiktokReport = dataToShow.some((offer) => {
      const selectedDeliverables = offer.deliverables
        .filter((deliverable) => deliverable.selectable && checkedIds.includes(deliverable.id));

      return selectedDeliverables.some((deliverable) => (
        deliverable.deliverableType === DELIVERABLE_TYPE_TIKTOK_VIDEO
      ));
    });

    return (
      <BaseReportingPage
        headline={title}
        params={params}
      >
        <h1 className="report-download-page__title">
          {title}
        </h1>

        <VerticalSpacing type="large" />

        <section className="report-download-page__top-section">
          <h2 className="report-download-page__subtitle">
            {__t('report_download_page.filter_subtitle')}
          </h2>

          <VerticalSpacing type="large" />

          <ReportDownloadFilters
            location={location}
            groupedDeliverables={groupedDeliverables}
            startDate={campaignStartDate}
            endDate={campaignEndDate}
            isDisplaySelectedOnly={isDisplaySelectedOnly}
            onChange={this.handleFiltersChange}
          />

          <VerticalSpacing type="large" />

          <ReportDownloadMetricsSummary
            splits={splits}
            groupedDeliverables={dataToShow}
            checkedIds={checkedIds}
          />
        </section>

        <VerticalSpacing type="medium" />

        <section className="report-download-page__subtitle-section">
          <h2 className="report-download-page__subtitle">
            {__t('report_download_page.tasks_subtitle')}
          </h2>
          <CampaignReportDownloadButton
            withPPTDownload={isInstagramReport || isTiktokReport}
            onDownloadExcel={this.handleDownloadExcel}
            onDownloadPPT={this.handleDownloadPPT}
            isDisabled={checkedIds.length === 0}
            isDownloading={isDownloading}
          />
        </section>

        <VerticalSpacing type="small" />

        <ReportDownloadActions
          isAlreadyExpanded={isAlreadyExpanded}
          isAlreadySelected={isAlreadySelected}
          onToggleDisplaySelected={this.handleToggleSelectedOnly}
          onSelectAll={this.handleSelectAllMatched}
          onDeselectAll={this.handleDeSelectAllMatched}
          onExpandAll={() => this.handleItemExpandToggleAll(true)}
          onCollapseAll={() => this.handleItemExpandToggleAll(false)}
        />

        <VerticalSpacing type="medium" />

        <DeliverablesRowsCustomReport
          includeLocation
          campaignId={campaignId}
          itemClassName="report-download-page__creator-item-wrapper"
          visibleGroupedDeliverables={dataToShow}
          allGroupedDeliverables={groupedDeliverables}
          expandedIds={expandedIds}
          isZalandoOutfitOnly={false} // TODO: set this dynamically if necessary. see `deliverables.ts` -> `isZalandoutOutfitOnly()`
          onToggleExpanded={this.handleItemExpandToggle}
          checkedDeliverables={checkedIds}
          onToggleCreatorSelection={this.handleCreatorToggle}
          onToggleDeliverableSelection={this.handleDeliverableToggle}
          includeRatio
        />
      </BaseReportingPage>
    );
  }
}

const mapStateToProps = ({ campaignCostAttribution, campaignCustomReport, campaignBrief, router }): StateProps => ({
  splits: campaignCostAttribution.results,
  isSplitsLoading: campaignCostAttribution.isLoading,
  location: router.location,
  isReportLoading: campaignCustomReport.isLoading,
  isDownloading: campaignCustomReport.isDownloading,
  groupedDeliverables: campaignCustomReport.groupedDeliverables,
  campaignStartDate: campaignBrief?.brief?.startDate,
  campaignEndDate: campaignBrief?.brief?.endDate
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchSplits: (campaignId: number) => dispatch(fetchSplits(campaignId)),
  dispatchResetSplits: () => dispatch(resetSplits()),

  dispatchDownloadCustomReport: (
    campaignId: number,
    checkedIds: Array<number>,
    type: CustomReportDownloadType
  ) => dispatch(generateCustomReportAsync(campaignId, checkedIds, type)),
  dispatchReset: () => dispatch(resetCampaignCustomReport()),

  dispatchFetchCustomReportDeliverables: (campaignId: number) =>
    dispatch(fetchCustomReportDeliverables(campaignId)),
  dispatchFetchInfluncerMetrics: (campaignId: number) => dispatch(fetchInfluncerMetrics(campaignId))

});

export default connect(mapStateToProps, mapDispatchToProps)(ReportDownloadPage);

